import { z } from 'zod';
import { PageRoute, Uuid } from './blocks.js';
import { JournalProjection, NavigationProjection, PageProjection, PricingProjection, ShootProjection, SiteSettingsProjection, Slug, } from './content.js';
import { ReleasedAsset } from './assets.js';
import { ReleaseTemplateProjectionV1 } from './templates.js';
const fixedRoutes = [...PageRoute.options];
export const ReleaseRedirect = z
    .object({
    source: z.string().startsWith('/'),
    destination: z.string().startsWith('/'),
    status: z.literal(301),
})
    .strict();
export const ContentReleaseV1 = z
    .object({
    schemaVersion: z.literal(1),
    audience: z.enum(['Public', 'Preview']).default('Public'),
    releaseId: Uuid,
    createdAt: z.iso.datetime(),
    globals: SiteSettingsProjection,
    navigation: NavigationProjection,
    templateContent: ReleaseTemplateProjectionV1,
    pages: z.record(PageRoute, PageProjection),
    pricing: PricingProjection,
    shoots: z.record(Slug, ShootProjection),
    journalArticles: z.record(Slug, JournalProjection),
    redirects: z.array(ReleaseRedirect).default([]),
    assets: z.record(Uuid, ReleasedAsset),
    sourceRevisions: z.record(z.string(), z.number().int().nonnegative()),
})
    .strict()
    .superRefine((release, ctx) => {
    for (const [key, asset] of Object.entries(release.assets)) {
        if (key !== asset.assetId) {
            ctx.addIssue({
                code: 'custom',
                path: ['assets', key, 'assetId'],
                message: 'Release asset key must match assetId',
            });
        }
        if (asset.variants) {
            if (!asset.variants.includes(asset.assetId)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['assets', key, 'variants'],
                    message: 'Responsive variants must include the canonical asset',
                });
            }
            if (new Set(asset.variants).size !== asset.variants.length) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['assets', key, 'variants'],
                    message: 'Responsive variants must be unique',
                });
            }
            for (const variantId of asset.variants) {
                if (!(variantId in release.assets)) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['assets', key, 'variants'],
                        message: 'Responsive variant is missing from release assets',
                    });
                }
            }
        }
    }
    const validateAssetRefs = (value, path) => {
        if (Array.isArray(value)) {
            value.forEach((entry, index) => validateAssetRefs(entry, [...path, index]));
            return;
        }
        if (!value || typeof value !== 'object')
            return;
        const record = value;
        if (typeof record.assetId === 'string' && !(record.assetId in release.assets)) {
            ctx.addIssue({
                code: 'custom',
                path: [...path, 'assetId'],
                message: 'AssetRef is missing from release assets',
            });
        }
        for (const [key, nested] of Object.entries(record)) {
            validateAssetRefs(nested, [...path, key]);
        }
    };
    validateAssetRefs(release.globals, ['globals']);
    validateAssetRefs(release.templateContent.media, ['templateContent', 'media']);
    validateAssetRefs(release.pages, ['pages']);
    validateAssetRefs(release.pricing, ['pricing']);
    validateAssetRefs(release.shoots, ['shoots']);
    validateAssetRefs(release.journalArticles, ['journalArticles']);
    const releaseShootKeys = Object.keys(release.shoots).sort();
    const templateShootKeys = Object.keys(release.templateContent.shoots).sort();
    if (releaseShootKeys.length !== templateShootKeys.length ||
        releaseShootKeys.some((key, index) => key !== templateShootKeys[index])) {
        ctx.addIssue({
            code: 'custom',
            path: ['templateContent', 'shoots'],
            message: 'Release template shoot keys must match release shoots',
        });
    }
    const releaseJournalKeys = Object.keys(release.journalArticles).sort();
    const templateJournalKeys = new Set(release.templateContent.journalArticles
        .map((entry) => entry.slug)
        .filter((slug) => typeof slug === 'string'));
    if (releaseJournalKeys.some((key) => !templateJournalKeys.has(key))) {
        ctx.addIssue({
            code: 'custom',
            path: ['templateContent', 'journalArticles'],
            message: 'Release template must represent every released journal article',
        });
    }
    for (const key of templateJournalKeys) {
        if (!(key in release.journalArticles)) {
            ctx.addIssue({
                code: 'custom',
                path: ['templateContent', 'journalArticles'],
                message: 'Release template journal slug is missing from release articles',
            });
        }
    }
    const releasePlanKeys = release.pricing.plans.map((plan) => plan.key).sort();
    const templatePlanKeys = Object.keys(release.templateContent.pricingPackages).sort();
    if (releasePlanKeys.length !== templatePlanKeys.length ||
        releasePlanKeys.some((key, index) => key !== templatePlanKeys[index])) {
        ctx.addIssue({
            code: 'custom',
            path: ['templateContent', 'pricingPackages'],
            message: 'Release template pricing keys must match release plans',
        });
    }
    for (const [key, presentation] of Object.entries(release.templateContent.pricingPackages)) {
        if (key !== presentation.planKey) {
            ctx.addIssue({
                code: 'custom',
                path: ['templateContent', 'pricingPackages', key, 'planKey'],
                message: 'Pricing presentation key must match planKey',
            });
        }
    }
    const portfolioItems = release.pages['/portfolio']?.blocks.flatMap((block) => block.enabled && block.type === 'Gallery' ? block.payload.items : []);
    const releasePortfolioKeys = (portfolioItems ?? [])
        .map((item) => item.key)
        .filter((key) => typeof key === 'string')
        .sort();
    const templatePortfolioKeys = Object.keys(release.templateContent.portfolioItems).sort();
    if (releasePortfolioKeys.length !== templatePortfolioKeys.length ||
        releasePortfolioKeys.some((key, index) => key !== templatePortfolioKeys[index])) {
        ctx.addIssue({
            code: 'custom',
            path: ['templateContent', 'portfolioItems'],
            message: 'Release template portfolio keys must match enabled gallery items',
        });
    }
    for (const [key, presentation] of Object.entries(release.templateContent.portfolioItems)) {
        if (key !== presentation.itemKey) {
            ctx.addIssue({
                code: 'custom',
                path: ['templateContent', 'portfolioItems', key, 'itemKey'],
                message: 'Portfolio presentation key must match itemKey',
            });
        }
    }
    const routes = new Set();
    for (const route of fixedRoutes) {
        const page = release.pages[route];
        if (!page) {
            ctx.addIssue({
                code: 'custom',
                path: ['pages', route],
                message: 'Missing fixed page route',
            });
        }
        else if (page.route !== route) {
            ctx.addIssue({
                code: 'custom',
                path: ['pages', route, 'route'],
                message: 'Fixed page key must match projected route',
            });
        }
        routes.add(route);
    }
    const addDynamicRoute = (route, path) => {
        if (routes.has(route)) {
            ctx.addIssue({
                code: 'custom',
                path,
                message: 'Dynamic route collides with an existing route',
            });
            return;
        }
        routes.add(route);
    };
    for (const [key, shoot] of Object.entries(release.shoots)) {
        const expectedRoute = `/portfolio/${shoot.slug}`;
        if (key !== shoot.slug) {
            ctx.addIssue({
                code: 'custom',
                path: ['shoots', key, 'slug'],
                message: 'Shoot key must match slug',
            });
        }
        if (shoot.route !== expectedRoute) {
            ctx.addIssue({
                code: 'custom',
                path: ['shoots', key, 'route'],
                message: 'Shoot route must be computed from slug',
            });
        }
        addDynamicRoute(shoot.route, ['shoots', key, 'route']);
    }
    for (const [key, article] of Object.entries(release.journalArticles)) {
        const expectedRoute = `/journal/${article.slug}`;
        if (key !== article.slug) {
            ctx.addIssue({
                code: 'custom',
                path: ['journalArticles', key, 'slug'],
                message: 'Journal key must match slug',
            });
        }
        if (article.route !== expectedRoute) {
            ctx.addIssue({
                code: 'custom',
                path: ['journalArticles', key, 'route'],
                message: 'Journal route must be computed from slug',
            });
        }
        addDynamicRoute(article.route, ['journalArticles', key, 'route']);
    }
    for (const [itemIndex, item] of (portfolioItems ?? []).entries()) {
        if (!item.destination || item.external === undefined)
            continue;
        const isExternalDestination = !item.destination.startsWith('/');
        const itemPath = ['pages', '/portfolio', 'blocks', 'Gallery', 'items', itemIndex];
        if (item.external !== isExternalDestination) {
            ctx.addIssue({
                code: 'custom',
                path: [...itemPath, 'external'],
                message: 'Portfolio external flag must match destination kind',
            });
        }
        if (!isExternalDestination) {
            const [route] = item.destination.split('?');
            if (!routes.has(route)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...itemPath, 'destination'],
                    message: 'Portfolio destination must resolve to a released route',
                });
            }
        }
    }
    const redirectSources = new Set();
    for (const [index, redirect] of release.redirects.entries()) {
        if (redirectSources.has(redirect.source)) {
            ctx.addIssue({
                code: 'custom',
                path: ['redirects', index, 'source'],
                message: 'Redirect source must be unique',
            });
        }
        redirectSources.add(redirect.source);
        if (redirect.source === redirect.destination) {
            ctx.addIssue({
                code: 'custom',
                path: ['redirects', index],
                message: 'Redirect source and destination must differ',
            });
        }
    }
});
